import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import {
  areaDaRota,
  encerrarSessao,
  gravarCrecheSelecionada,
  gravarLgpdPendente,
  iniciarSessao,
  lerLgpdPendente,
  lerToken,
} from '../services/sessao';

interface Usuario {
  id: number;
  email: string;
  roles: string[];
  id_creche: number;
  // `id_plano` FOI REMOVIDO daqui de propósito. O campo nunca existiu no token
  // nem na entidade do usuário: `payload.id_plano` era sempre `undefined` e caía
  // em 'BRONZE', fazendo toda creche parecer Bronze — o menu escondia os módulos
  // Prata de quem pagava por eles. O plano é da CRECHE, não do usuário: use o
  // hook `usePlano()`, que lê a assinatura. Mantê-lo aqui devolvendo um valor
  // falso convidaria o próximo a cair no mesmo buraco.
  id_turma?: number;
  id_aluno?: number;
  tipo_usuario?: string;
}

export interface TermoLgpdPendente {
  id: number;
  titulo: string;
  versao: number;
}

interface AuthContextData {
  usuario: Usuario | null;
  token: string | null;
  isAutenticado: boolean;
  isCarregando: boolean;
  lgpdPendente: TermoLgpdPendente | null;
  login: (email: string, senha: string) => Promise<void>;
  logout: () => void;
  registrarConsentimentoLgpd: (idTermo: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

function decodificarToken(token: string): Usuario | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      id: payload.sub,
      email: payload.email,
      roles: payload.roles ?? [],
      id_creche: payload.id_creche ?? 0,
      id_turma: payload.id_turma ?? undefined,
      id_aluno: payload.id_aluno ?? undefined,
      tipo_usuario: payload.tipo_usuario ?? payload.roles?.[0] ?? '',
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // A sessão é por (aba × produto): `/app` e `/saas` têm tokens próprios, então
  // o contexto lê sempre o token da ÁREA da rota atual. `versao` só força a
  // releitura após login/logout — o storage é a fonte de verdade.
  const { pathname } = useLocation();
  const area = areaDaRota(pathname);
  const [versao, setVersao] = useState(0);
  const [isCarregando, setIsCarregando] = useState(false);

  const token = useMemo(() => lerToken(area), [area, versao]);
  const usuario = useMemo(() => (token ? decodificarToken(token) : null), [token]);
  const lgpdPendente = useMemo<TermoLgpdPendente | null>(() => {
    try {
      const raw = lerLgpdPendente(area);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [area, versao]);

  const login = useCallback(async (email: string, senha: string) => {
    setIsCarregando(true);
    try {
      const { data } = await api.post<{
        access_token: string;
        requer_consentimento_lgpd?: boolean;
        termo_lgpd_pendente?: TermoLgpdPendente | null;
      }>('/auth/login', { email, senha });

      const { access_token, requer_consentimento_lgpd, termo_lgpd_pendente } = data;
      iniciarSessao(access_token, area);
      // Evita que a creche selecionada por outro usuário (admin global) nesta
      // aba vaze para a sessão que está começando agora.
      gravarCrecheSelecionada(null, area);

      const pendente = requer_consentimento_lgpd ? (termo_lgpd_pendente ?? null) : null;
      gravarLgpdPendente(pendente ? JSON.stringify(pendente) : null, area);
      setVersao((v) => v + 1);
    } finally {
      setIsCarregando(false);
    }
  }, [area]);

  const registrarConsentimentoLgpd = useCallback(async (idTermo: number) => {
    await api.post('/lgpd/primeiro-acesso/aceitar', { id_termo: idTermo });
    gravarLgpdPendente(null, area);
    setVersao((v) => v + 1);
  }, [area]);

  const logout = useCallback(() => {
    // Encerra só a sessão DESTE produto NESTA aba — a creche aberta em outra
    // aba (ou o painel SaaS) continua logada.
    encerrarSessao(area);
    setVersao((v) => v + 1);
  }, [area]);

  return (
    <AuthContext.Provider value={{ usuario, token, isAutenticado: !!token, isCarregando, lgpdPendente, login, logout, registrarConsentimentoLgpd }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
